// src/game/board.ts
// Geometry & gates (LOCKED contract §5). Board is immutable and shared by reference.

import type { Cell, Color, Direction, Side } from './types';
import { DELTAS, SIDE_EXIT_DIRECTION } from './types';

/**
 * A gate is a perimeter opening in the wall (v1, §2.1).
 *   `side`  — which perimeter edge it sits on.
 *   `line`  — index of the first covered segment along that edge:
 *               top/bottom -> COLUMN index in [0, cols)
 *               left/right -> ROW    index in [0, rows)
 *   `span`  — number of consecutive segments covered (>= 1).
 *               A span-1 gate is the original 1-cell-wide gate.
 *               Covered segments: line .. line+span-1.
 * A matching block exits by moving in SIDE_EXIT_DIRECTION[side].
 * INVARIANT: every gate's (side, line) is unique within a Board, and `line` is in range.
 */
export interface Gate {
  readonly color: Color;
  readonly side: Side;
  readonly line: number;
  readonly span: number;
}

/**
 * Static level geometry. Immutable for a level's lifetime; shared by reference across all
 * GameState snapshots (zero copy cost). Interior fully open in v1 (no interior walls);
 * perimeter solid except at `gates`.
 */
export interface Board {
  readonly rows: number;
  readonly cols: number;
  readonly gates: readonly Gate[];
}

export function createBoard(rows: number, cols: number, gates: readonly Gate[]): Board {
  return { rows, cols, gates };
}

/** True iff cell is within [0,rows) x [0,cols). */
export function isInside(board: Board, cell: Cell): boolean {
  return cell.row >= 0 && cell.row < board.rows && cell.col >= 0 && cell.col < board.cols;
}

/** True iff cell is inside AND on the outermost ring (row/col === 0 or rows-1/cols-1). */
export function isPerimeter(board: Board, cell: Cell): boolean {
  if (!isInside(board, cell)) {
    return false;
  }
  return (
    cell.row === 0 ||
    cell.row === board.rows - 1 ||
    cell.col === 0 ||
    cell.col === board.cols - 1
  );
}

/** Translate a cell by k steps in dir (k may be 0 or negative). Default k = 1. */
export function step(cell: Cell, dir: Direction, k: number = 1): Cell {
  const d = DELTAS[dir];
  return { row: cell.row + d.drow * k, col: cell.col + d.dcol * k };
}

/**
 * The in-board cell directly inside a gate at its first (`line`) segment (legacy / span-1 compat).
 * Callers that need all covered cells use gateInnerCells instead.
 *   top    line=c -> { row: 0,      col: c }
 *   bottom line=c -> { row: rows-1, col: c }
 *   left   line=r -> { row: r,      col: 0 }
 *   right  line=r -> { row: r,      col: cols-1 }
 */
export function gateInnerCell(board: Board, gate: Gate): Cell {
  switch (gate.side) {
    case 'top':
      return { row: 0, col: gate.line };
    case 'bottom':
      return { row: board.rows - 1, col: gate.line };
    case 'left':
      return { row: gate.line, col: 0 };
    case 'right':
      return { row: gate.line, col: board.cols - 1 };
    default: {
      const _exhaustive: never = gate.side;
      return _exhaustive;
    }
  }
}

/** All line indices covered by the gate: [line, line+1, ..., line+span-1]. */
export function gateLines(gate: Gate): readonly number[] {
  const lines: number[] = [];
  for (let i = 0; i < gate.span; i++) {
    lines.push(gate.line + i);
  }
  return lines;
}

/**
 * Inmost in-board cell for each covered line of the gate.
 *   top:    { row: 0,        col: l } for each l in gateLines(gate)
 *   bottom: { row: rows-1,   col: l }
 *   left:   { row: l,        col: 0 }
 *   right:  { row: l,        col: cols-1 }
 */
export function gateInnerCells(board: Board, gate: Gate): readonly Cell[] {
  return gateLines(gate).map((l) => {
    switch (gate.side) {
      case 'top':
        return { row: 0, col: l };
      case 'bottom':
        return { row: board.rows - 1, col: l };
      case 'left':
        return { row: l, col: 0 };
      case 'right':
        return { row: l, col: board.cols - 1 };
      default: {
        const _exhaustive: never = gate.side;
        return _exhaustive;
      }
    }
  });
}

/**
 * True iff every lane in `lanes` is covered by this gate (i.e., ∈ gateLines(gate)).
 */
export function gateCoversLanes(gate: Gate, lanes: readonly number[]): boolean {
  const lines = gateLines(gate);
  return lanes.every((l) => lines.includes(l));
}

/**
 * True iff `offCell` is an off-board crossing cell of `gate` for a block moving `dir`.
 * Precondition: dir === SIDE_EXIT_DIRECTION[gate.side] (verified defensively).
 * offCell must be one step past the perimeter on gate.side, at a perpendicular index
 * that is within gateLines(gate). Crossing cells for each side:
 *   top:    { row: -1,    col: l } for l ∈ gateLines(gate), dir 'up'
 *   bottom: { row: rows,  col: l } for l ∈ gateLines(gate), dir 'down'
 *   left:   { row: l, col: -1   } for l ∈ gateLines(gate), dir 'left'
 *   right:  { row: l, col: cols } for l ∈ gateLines(gate), dir 'right'
 */
export function isGateCrossing(board: Board, gate: Gate, dir: Direction, offCell: Cell): boolean {
  if (dir !== SIDE_EXIT_DIRECTION[gate.side]) {
    return false;
  }
  const lines = gateLines(gate);
  switch (gate.side) {
    case 'top':
      return offCell.row === -1 && lines.includes(offCell.col);
    case 'bottom':
      return offCell.row === board.rows && lines.includes(offCell.col);
    case 'left':
      return offCell.col === -1 && lines.includes(offCell.row);
    case 'right':
      return offCell.col === board.cols && lines.includes(offCell.row);
    default: {
      const _exhaustive: never = gate.side;
      return _exhaustive;
    }
  }
}

/** The gate (if any) on `side` at position `line`; undefined if none. */
export function gateOn(board: Board, side: Side, line: number): Gate | undefined {
  return board.gates.find((g) => g.side === side && g.line === line);
}
